import React from 'react';
import AtlasV2DorisDoubleDropdownChart from 'src/components/AtlasV2Components/AtlasV2graphs/AtlasV2DorisDoubleDropdownChart';
import { OTHER_TRAFFIC_FILED_IDS } from 'src/components/AtlasV2Components/AtlasV2TrafficPageComponents/AtlasV2TotalTraffic/AtlasV2TotalTrafficTopEntity';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import useTrafficMarketShareCalculator from 'src/components/AtlasV2Components/Hooks/useTrafficMarketShareCalculator';
import {
  useTrafficMetricsStore,
  useTrafficMetricsWithFilters
} from 'src/zustandStores/TrafficMetrics/useTrafficMetricsStore';
import { useDorisEntityDropdownOptions } from 'src/components/AtlasV2Components/AtlasV2TrafficV2PageComponents/AtlasV2TotalTrafficV2/AtlasV2TotalTrafficDoubleDropdownTopEntity';
import { GroupByField } from 'src/components/AtlasV2Components/AtlasV2TrafficV2PageComponents/AtlasV2TotalTrafficV2/AtlasV2TrafficDropdownTopEntityTraffic';
import {
  useEntityPhrasing,
  useTaxonomySubtitleText
} from 'src/components/BeaconV2Components/common/BeaconProDynamicSubtitle/hooks';
import { useMainTimePeriodShortDisplayName } from 'src/utils/Hooks/typedHooks';
import { useComparisonTimePeriodShortDisplayName } from 'src/utils/Hooks';
import SubtitleFilters from 'src/components/Charts/SubtitleFilters';
import { BeaconSubtitleContainer } from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconSubtitleContainer';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import SplineChartLoading from 'src/components/BeaconV2Components/SplineChartLoading/SplineChartLoading';
/**
 * Component that displays a double dropdown chart for organic traffic data.
 * Uses a custom market share calculator that gets denominator values from the TrafficMetrics Zustand store.
 */
const AtlasV2OrganicTrafficDoubleDropdownTopEntity = ({ groupByFields }: { groupByFields?: GroupByField[] }) => {
  // Get the data fetcher and loading state from the metrics fetcher hook
  const { isLoading: isDataFetching } = useTrafficMetricsWithFilters();
  const dropdownOptions = useDorisEntityDropdownOptions();
  const entityPhrase = useEntityPhrasing();
  const mainTimePeriodShortDisplayName = useMainTimePeriodShortDisplayName();
  const comparisonTimeDisplayName = useComparisonTimePeriodShortDisplayName();

  // Get loading state from the metrics store
  const isMetricsLoading = useTrafficMetricsStore((state) => state.isLoading);

  // Get the market share calculator and data loaded state for organic clicks
  const { calculateMarketShare, isDataLoaded } = useTrafficMarketShareCalculator({
    trafficType: 'organicClicks'
  });
  const taxonomyText = useTaxonomySubtitleText();

  // Calculate the overall loading state
  const isTrafficDataLoading = isMetricsLoading || isDataFetching || !isDataLoaded;

  return (
    <AtlasV2DorisDoubleDropdownChart
      groupByFields={groupByFields || dropdownOptions}
      fields={[
        {
          fieldName: 'organicClicks',
          label: 'Organic Traffic',
          metricType: 'organicClicks',
          metricTypeDisplay: METRICTYPE.VOLUME
        },
        {
          fieldName: 'organicClicksShare',
          label: 'Share of Organic Traffic',
          metricType: 'organicClicks',
          isMarketShare: true,
          metricTypeDisplay: METRICTYPE.PERCENT,
          // Pass custom market share calculator
          marketShareCalculator: calculateMarketShare
        }
      ]}
      queryKey="organicClicks_doubleDropdown"
      endPoint="GetTrafficByType"
      isLoading={isTrafficDataLoading}
      preProcessData={(data) => {
        const otherTraffics = data.filter((datum) => OTHER_TRAFFIC_FILED_IDS.includes(datum.fieldId));
        const remainingData = data.filter((datum) => !OTHER_TRAFFIC_FILED_IDS.includes(datum.fieldId));
        return [...remainingData, ...otherTraffics];
      }}
      beaconChartWithLegendPropsBuilder={(props) => {
        return {
          ...props,
          loadingComponent: <SplineChartLoading includeSubtitle />,
          includeSummaryInfo: false,
          subtitle: (
            <BeaconSubtitleContainer>
              <Text variant="body1" lineHeight="24px">
                {`The chart displays the ${props.selectedField.label.toLocaleLowerCase()} for ${entityPhrase} in each ${props.selectedGroupByField.toLocaleLowerCase()} ${taxonomyText} during the ${mainTimePeriodShortDisplayName} period compared to the ${comparisonTimeDisplayName}.`}
                <SubtitleFilters showFilterNumber={false} />
              </Text>
            </BeaconSubtitleContainer>
          )
        };
      }}
    />
  );
};

export default AtlasV2OrganicTrafficDoubleDropdownTopEntity;
